import maplibregl, { type Map as MapLibreMap } from 'maplibre-gl'

/** Site-wide public branding; unrelated to logged-in identity or notification preferences. */
export type PublicAgent = {
  name: string
  firstName: string
  lastName: string
  mlsId: string
  phoneNumber: string
  broker: string
  brokerMlsId: string
  logoConfigured: boolean
  /** Absolute public image URL, or an empty string when no logo is configured. */
  logoUrl: string
}
export type PublicConfigResponse = {
  siteId: string
  publicToken: string
  apiBaseUrl: string
  agentSitePublicUrl: string
  turnstileSiteKey: string
  features: string[]
  agent: PublicAgent
}

export type AgentOSClientOptions = {
  apiBaseUrl?: string
  siteId?: string
  token?: string
  sessionTimeoutMs?: number
}

declare global {
  var __AGENTOS_SDK_CONFIG__: { apiBaseUrl?: string; siteId?: string; publicToken?: string; turnstileSiteKey?: string } | undefined
}

export const listingSearchSorts = ['updated-desc', 'price-asc', 'price-desc'] as const

export type ListingSearchSort = typeof listingSearchSorts[number]

export type ListingSearch = {
  query?: string
  minPrice?: number
  maxPrice?: number
  beds?: number
  baths?: number
  counties?: string[]
  cities?: string[]
  statuses?: string[]
  propertyTypes?: string[]
  providerIds?: string[]
  sort?: ListingSearchSort
}

export type MapBounds = {
  west: number
  south: number
  east: number
  north: number
}

export type ListingSearchOptions = {
  cities: string[]
  counties: string[]
  propertyTypes: string[]
  statuses: string[]
  providers: Array<{ id: string; name: string }>
  sorts: ListingSearchSortOption[]
}

export type ListingSearchSortOption = { value: ListingSearchSort; label: string }

export type ListingSearchConfig = {
  search: ListingSearch
  pagination: ListingSearchPagination
}

export type ListingSearchPagination = { page: number; pageSize: number }

export type MapConfig = {
  bounds: MapBounds
  centerLat: number
  centerLng: number
  zoom: number
  maptilerKey: string
  updatedAt: string
}

export type ListingGeoJSONFeature = {
  type: 'Feature'
  id?: string
  geometry: { type: 'Point'; coordinates: [number, number] }
  properties: MapListing
}

export type MapListing = {
  id: string
  provider: string
  providerId: string
  status: string
  price: number
  beds: number
  baths: number
  sqft: number
  address: string
  city: string
  state: string
  postalCode: string
  propertyType: string
  media: string[]
  priceLabel?: string
}

export type ListingGeoJSON = {
  type: 'FeatureCollection'
  bbox?: number[]
  features: ListingGeoJSONFeature[]
  total: number
  truncated: boolean
}

export type ListingSearchProviderMetadata = {
  name: string
  lastSyncAt: string | null
}

export type ListingSearchMetadata = {
  mlsProviders: Record<string, ListingSearchProviderMetadata>
}

export type AgentOSMapOptions = {
  markerColors?: Record<string, string>
  defaultMarkerColor?: string
  listingUrlTemplate?: string
  popup?: AgentOSMapPopupOptions
  injectMapLibreCSS?: boolean
  mapLibreOptions?: Record<string, unknown>
}

export type AgentOSMapPopupOptions = {
  showPrice?: boolean
  showStats?: boolean
  showStatus?: boolean
  showImage?: boolean
  imageField?: string
  imageHeight?: number | string
  fontFamily?: string
  textColor?: string
  mutedTextColor?: string
  backgroundColor?: string
  accentColor?: string
  borderRadius?: number | string
  padding?: number | string
  maxWidth?: number | string
  className?: string
  linkLabel?: string
  template?: string
}

type NormalizedMapPopupOptions = Required<AgentOSMapPopupOptions>

export type AgentOSListingMapOptions = AgentOSMapOptions & {
  zoom?: number
  openPopup?: boolean
}

export type ListingMapController = {
  map: MapLibreMap
  destroy: () => void
}

export type ListingSearchResponse = {
  items: Listing[]
  total: number
  limit: number
  offset: number
  map: ListingGeoJSON | null
  metadata: ListingSearchMetadata
}

export type ListingDetailResponse = Listing & {
  metadata: ListingSearchMetadata
}

export type ListingSearchState = {
  search: ListingSearch
  pagination: ListingSearchPagination & { totalPages: number }
  listings: { items: Listing[]; total: number }
  metadata: ListingSearchMetadata
  loading: boolean
  error: unknown | null
  map?: {
    bounds: MapBounds
    count: number
    total: number
    truncated: boolean
    loading: boolean
    error: unknown | null
  }
}

export type ListingSearchPreference = {
  search: ListingSearch
  page: number
  bounds?: MapBounds
}

export type ListingSearchPreferenceStore = {
  load: () => Promise<ListingSearchPreference | null>
  save: (preference: ListingSearchPreference) => Promise<void>
  clear: () => Promise<void>
}

export type LocalSearchPreferences = ListingSearchPreferenceStore & {
  reset: () => Promise<void>
}

export type ListingSearchMapOptions = AgentOSMapOptions & {
  target: string | HTMLElement
  initialBounds?: MapBounds
  moveEndDebounceMs?: number
}

export type CreateListingSearchOptions = {
  search: ListingSearch
  pagination: ListingSearchPagination
  map?: ListingSearchMapOptions
}

export type ListingSearchController = {
  readonly map?: MapLibreMap
  getState: () => ListingSearchState
  subscribe: (listener: (state: ListingSearchState) => void) => () => void
  update: (search: ListingSearch, options?: { page?: number; bounds?: MapBounds }) => Promise<void>
  setPage: (page: number) => Promise<void>
  refresh: () => Promise<void>
  destroy: () => void
}

export type FeaturedListingsParams = {
  limit?: number
}

export type Listing = {
  id: string
  provider: string
  providerId: string
  originProviderId?: string
  originProviderName?: string
  status: string
  price: number
  beds: number
  baths: number
  sqft: number
  address: string
  city: string
  county: string
  state: string
  postalCode: string
  propertyType: string
  latitude: number
  longitude: number
  media: string[]
  description?: string
  yearBuilt?: number
  lotSizeSquareFeet?: number
  lotSizeAcres?: number
  garageSpaces?: number
  agent?: ListingContact
  office?: ListingContact
  features?: ListingFeatures
  raw?: Record<string, unknown>
}

export type ListingMediaAsset = {
  mediaKey: number
  kind: 'image' | 'video' | 'virtualTour' | 'externalTour' | 'external' | 'unknown'
  provider?: string
  url: string
  thumbnailUrl?: string
  contentType?: string
  mediaType?: string
  mediaCategory?: string
  title?: string
  description?: string
  externalKey?: string
  imageOf?: string
  resourceName?: string
  width?: number
  height?: number
  displayOrder: number
  preferredPhoto: boolean
}

export type ListingMediaResponse = {
  listingId: string
  listingKey: number
  items: ListingMediaAsset[]
}

export type ListingContact = {
  name?: string
  email?: string
  phone?: string
}

export type ListingFeatures = {
  interior?: string[]
  exterior?: string[]
  appliances?: string[]
  basement?: string[]
  cooling?: string[]
  heating?: string[]
  parking?: string[]
  roof?: string[]
  laundry?: string[]
  sewer?: string[]
  waterSource?: string[]
  style?: string[]
  levels?: string[]
  utilities?: string[]
  otherFeatures?: string[]
}

export type ListingResponse = {
  items: Listing[]
  total: number
  limit: number
  offset: number
}

export type TrackEvent = {
  event: string
  listingId?: string
  path?: string
  metadata?: Record<string, unknown>
}

export type ContactRequest = {
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  details?: string
  path?: string
  turnstileToken?: string
  metadata?: Record<string, unknown>
}

export const featuredAnchorId = 'agentos-featured'
export const analyticsDisabledStorageKey = 'agentos:analyticsDisabled'
const defaultSessionTimeoutMs = 30 * 60 * 1000

export function createClient(options: AgentOSClientOptions = {}) {
  setAnalyticsDisabledFromURL()
  const injected = globalThis.__AGENTOS_SDK_CONFIG__ ?? {}
  const configuredBaseUrl = options.apiBaseUrl ?? injected.apiBaseUrl
  const apiBaseUrl = configuredBaseUrl ? publicURLToAPIBaseURL(configuredBaseUrl) : inferAPIBaseURL().replace(/\/$/, '')
  const siteId = options.siteId ?? injected.siteId ?? 'demo-site'
  const token = options.token ?? injected.publicToken
  const turnstileSiteKey = injected.turnstileSiteKey ?? ''
  const visitor = getOrCreateVisitorId()
  const session = getOrCreateSessionId(options.sessionTimeoutMs ?? defaultSessionTimeoutMs)
  const visitorId = visitor.id
  const personId = visitor.id
  const sessionId = session.id
  let configuredMapOptions: AgentOSMapOptions = {}

  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const headers = new Headers(init?.headers)
    headers.set('Content-Type', 'application/json')
    if (token) {
      headers.set('X-AgentOS-Token', token)
    }
    const res = await fetch(`${apiBaseUrl}${path}`, { ...init, headers })
    if (!res.ok) {
      throw new Error(`AgentOS request failed: ${res.status}`)
    }
    return res.json() as Promise<T>
  }

  function track(event: string | TrackEvent, metadata: Record<string, unknown> = {}) {
    if (analyticsDisabled()) return Promise.resolve({ ok: true, skipped: true })
    const payload = typeof event === 'string' ? { event, metadata } : event
    return request('/t', {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        siteId,
        personId,
        visitorId,
        sessionId,
        referrer: documentReferrer(),
        path: payload.path ?? globalThis.location?.pathname ?? '',
      }),
    })
  }

  function contact(payload: ContactRequest) {
    return request<{ ok: boolean; id: string }>('/c', {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        siteId,
        personId,
        visitorId,
        sessionId,
        referrer: documentReferrer(),
        path: payload.path ?? globalThis.location?.pathname ?? '',
      }),
    })
  }

  if (session.isNew) {
    void track(visitor.isNew ? 'new_session' : 'resume_session', {
      referrer: documentReferrer(),
      entryPath: globalThis.location?.pathname ?? '',
      returningVisitor: !visitor.isNew,
      sessionReason: visitor.isNew ? 'new_person' : session.reason,
      previousSessionId: session.previousSessionId,
      timeoutMs: options.sessionTimeoutMs ?? defaultSessionTimeoutMs,
    }).catch(() => undefined)
  }

  async function getMapConfig() {
    return request<MapConfig>('/public/map-config')
  }

  function getListingSearchConfig() {
    return request<{
      filters: Omit<ListingSearch, 'sort'>
      sort: ListingSearchSort
      pageSize: number
      updatedAt: string
    }>('/public/listings/search-config').then((config): ListingSearchConfig => ({
      search: normalizeListingSearch({ ...config.filters, sort: config.sort }),
      pagination: { page: 1, pageSize: config.pageSize },
    }))
  }

  function getListingSearchOptions() {
    return request<ListingSearchOptions>('/public/listings/search-options')
  }

  function createListingSearch(options: CreateListingSearchOptions): Promise<ListingSearchController> {
    return createAgentOSListingSearch({
      options: {
        ...options,
        map: options.map ? mergeSearchMapOptions(configuredMapOptions, options.map) : undefined,
      },
      search: (value, pageSize, offset, bounds, signal) => request<ListingSearchResponse>(
        `/public/listings/search${listingSearchQuery(value, pageSize, offset, bounds)}`,
        { signal },
      ),
      getMapConfig,
      track,
    })
  }

  function createListingMap(target: string | HTMLElement, listing: Listing, mapOptions: AgentOSListingMapOptions = {}): Promise<ListingMapController> {
    return createAgentOSListingMap({
      target,
      listing,
      options: mergeMapOptions(configuredMapOptions, mapOptions) as AgentOSListingMapOptions,
      getMapConfig,
      track,
    })
  }

  function configureMap(mapOptions: AgentOSMapOptions = {}) {
    configuredMapOptions = mergeMapOptions(configuredMapOptions, mapOptions)
    return { ...configuredMapOptions }
  }

  return {
    siteId,
    personId,
    visitorId,
    sessionId,
    turnstileSiteKey,
    getConfig: () => request<PublicConfigResponse>('/public/config'),
    getMapConfig,
    getListingSearchConfig,
    getListingSearchOptions,
    getListing: (id: string) => request<ListingDetailResponse>(`/public/listings/${encodeURIComponent(id)}`),
    getListingMedia: (id: string) => request<ListingMediaResponse>(`/public/listings/${encodeURIComponent(id)}/media`),
    getFeaturedListings: (params: FeaturedListingsParams = {}) => request<ListingResponse>(`/public/featured-listings${toQuery(params)}`),
    configureMap,
    createListingSearch,
    createLocalSearchPreferences: ({ scope, version = 2 }: { scope: string; version?: number }) => createLocalStorageListingSearchPreferenceStore({ siteId, scope, version }),
    createListingMap,
    contact,
    markFeatured,
    track,
  }
}

async function createAgentOSListingSearch({
  options,
  search,
  getMapConfig,
  track,
}: {
  options: CreateListingSearchOptions
  search: (value: ListingSearch, pageSize: number, offset: number, bounds: MapBounds | undefined, signal: AbortSignal) => Promise<ListingSearchResponse>
  getMapConfig: () => Promise<MapConfig>
  track: (event: string | TrackEvent, metadata?: Record<string, unknown>) => Promise<unknown>
}): Promise<ListingSearchController> {
  const initialPagination = normalizePagination(options.pagination)
  let state: ListingSearchState = {
    search: normalizeListingSearch(options.search),
    pagination: { ...initialPagination, totalPages: 0 },
    listings: { items: [], total: 0 },
    metadata: { mlsProviders: {} },
    loading: false,
    error: null,
    map: undefined,
  }
  let mapData = emptyListingGeoJSON()
  let map: MapLibreMap | undefined
  let mapBounds: MapBounds | undefined
  let element: HTMLElement | undefined
  let debounceID: ReturnType<typeof setTimeout> | undefined
  let activeRequest: AbortController | undefined
  let elementClickHandler: ((event: MouseEvent) => void) | undefined
  let requestID = 0
  let destroyed = false
  const listeners = new Set<(state: ListingSearchState) => void>()

  const snapshot = (): ListingSearchState => ({
    ...state,
    search: cloneListingSearch(state.search),
    pagination: { ...state.pagination },
    listings: { ...state.listings, items: [...state.listings.items] },
    metadata: { mlsProviders: Object.fromEntries(Object.entries(state.metadata.mlsProviders).map(([id, provider]) => [id, { ...provider }])) },
    map: state.map ? { ...state.map, bounds: { ...state.map.bounds } } : undefined,
  })
  const publish = () => {
    if (destroyed) return
    const current = snapshot()
    listeners.forEach((listener) => listener(current))
  }
  const trackMapBounds = (bounds: MapBounds, resultCount?: number) => {
    void track('map_bounds_changed', {
      bounds: { ...bounds },
      filters: cloneListingSearch(state.search),
      ...(resultCount === undefined ? {} : { resultCount }),
    }).catch(() => undefined)
  }
  const execute = async (): Promise<void> => {
    if (destroyed) throw new Error('AgentOS listing search has been destroyed')
    activeRequest?.abort()
    const request = new AbortController()
    activeRequest = request
    const currentRequest = ++requestID
    const requestBounds = mapBounds ? { ...mapBounds } : undefined
    state = {
      ...state,
      loading: true,
      error: null,
      map: state.map ? { ...state.map, bounds: requestBounds ?? state.map.bounds, loading: true, error: null } : undefined,
    }
    publish()
    try {
      const { page, pageSize } = state.pagination
      const response = await search(state.search, pageSize, (page - 1) * pageSize, requestBounds, request.signal)
      if (destroyed || currentRequest !== requestID) return
      if (map) {
        mapData = addListingLabels(response.map ?? emptyListingGeoJSON())
        ensureListingLayers(map, mapData, options.map ?? {})
      }
      state = {
        ...state,
        pagination: { page, pageSize, totalPages: response.total === 0 ? 0 : Math.ceil(response.total / pageSize) },
        listings: { items: response.items, total: response.total },
        metadata: response.metadata,
        loading: false,
        map: state.map ? {
          bounds: requestBounds ?? state.map.bounds,
          count: response.map?.features.length ?? 0,
          total: response.map?.total ?? 0,
          truncated: response.map?.truncated ?? false,
          loading: false,
          error: null,
        } : undefined,
      }
      publish()
    } catch (error) {
      if (destroyed || currentRequest !== requestID || isAbortError(error)) return
      state = { ...state, loading: false, error, map: state.map ? { ...state.map, loading: false, error } : undefined }
      publish()
      throw error
    }
  }

  const controller: ListingSearchController = {
    get map() { return map },
    getState: snapshot,
    subscribe: (listener) => {
      listeners.add(listener)
      listener(snapshot())
      return () => listeners.delete(listener)
    },
    update: (next, updateOptions = {}) => {
      const page = updateOptions.page ?? 1
      if (!Number.isInteger(page) || page < 1) throw new Error('AgentOS listing search page must be a positive integer')
      if (updateOptions.bounds) {
        if (!map) throw new Error('AgentOS listing search cannot restore bounds without a map')
        const restoredBounds = normalizeMapBounds(updateOptions.bounds)
        map.fitBounds(boundsToLngLatBounds(restoredBounds), { duration: 0 }, { agentosRestore: true })
        mapBounds = boundsFromMap(map)
      }
      state = { ...state, search: normalizeListingSearch(next), pagination: { ...state.pagination, page } }
      return execute()
    },
    setPage: (page) => {
      if (!Number.isInteger(page) || page < 1) throw new Error('AgentOS listing search page must be a positive integer')
      state = { ...state, pagination: { ...state.pagination, page } }
      return execute()
    },
    refresh: execute,
    destroy: () => {
      if (destroyed) return
      destroyed = true
      requestID++
      activeRequest?.abort()
      if (debounceID) clearTimeout(debounceID)
      listeners.clear()
      map?.remove()
      if (elementClickHandler) element?.removeEventListener('click', elementClickHandler)
      element?.classList.remove('agentos-sdk-map')
    },
  }

  if (options.map) {
    element = typeof options.map.target === 'string' ? document.querySelector<HTMLElement>(options.map.target) ?? undefined : options.map.target
    if (!element) throw new Error('AgentOS map target was not found')
    if (options.map.injectMapLibreCSS !== false) injectMapLibreCSS()
    injectAgentOSMapCSS()
    element.classList.add('agentos-sdk-map')
    if (!element.style.height) element.style.height = '520px'
    const config = await getMapConfig()
    const initialBounds = options.map.initialBounds ? normalizeMapBounds(options.map.initialBounds) : undefined
    map = new maplibregl.Map({
      container: element,
      style: styleForMapTilerKey(config.maptilerKey),
      ...(initialBounds
        ? { bounds: boundsToLngLatBounds(initialBounds), fitBoundsOptions: { padding: 0 } }
        : { center: [config.centerLng, config.centerLat], zoom: config.zoom }),
      ...options.map.mapLibreOptions,
    })
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right')
    map.addControl(new maplibregl.ScaleControl({ unit: 'imperial' }), 'bottom-left')
    const currentMap = map
    const currentElement = element
    currentMap.on('styledata', () => ensureListingLayers(currentMap, mapData, options.map ?? {}))
    currentMap.on('click', (event) => openListingPopup(currentMap, event, options.map ?? {}, track))
    currentMap.on('mousemove', (event) => updateListingCursor(currentMap, event))
    elementClickHandler = (event) => trackListingLink(event, track)
    currentElement.addEventListener('click', elementClickHandler)

    try {
      await waitForMapLoad(currentMap)
    } catch (error) {
      currentElement.removeEventListener('click', elementClickHandler)
      currentElement.classList.remove('agentos-sdk-map')
      currentMap.remove()
      throw error
    }
    currentMap.resize()
    ensureListingLayers(currentMap, mapData, options.map)
    mapBounds = boundsFromMap(currentMap)
    state = {
      ...state,
      map: { bounds: { ...mapBounds }, count: 0, total: 0, truncated: false, loading: false, error: null },
    }
    await execute()
    trackMapBounds(mapBounds, state.map?.count ?? 0)
    void track('map_loaded', {
      filters: cloneListingSearch(state.search),
      resultCount: state.map?.count ?? 0,
    }).catch(() => undefined)

    currentMap.on('moveend', (event) => {
      if ((event as { agentosRestore?: boolean }).agentosRestore) return
      if (debounceID) clearTimeout(debounceID)
      debounceID = setTimeout(() => {
        mapBounds = boundsFromMap(currentMap)
        state = { ...state, pagination: { ...state.pagination, page: 1 } }
        trackMapBounds(mapBounds)
        void execute().catch(() => undefined)
      }, options.map?.moveEndDebounceMs ?? 250)
    })
  } else {
    await execute()
  }

  return controller
}

async function createAgentOSListingMap({
  target,
  listing,
  options,
  getMapConfig,
  track,
}: {
  target: string | HTMLElement
  listing: Listing
  options: AgentOSListingMapOptions
  getMapConfig: () => Promise<MapConfig>
  track: (event: string | TrackEvent, metadata?: Record<string, unknown>) => Promise<unknown>
}): Promise<ListingMapController> {
  const element = typeof target === 'string' ? document.querySelector<HTMLElement>(target) : target
  if (!element) {
    throw new Error('AgentOS map target was not found')
  }
  if (!Number.isFinite(listing.latitude) || !Number.isFinite(listing.longitude)) {
    throw new Error('AgentOS listing map requires listing latitude and longitude')
  }
  if (options.injectMapLibreCSS !== false) {
    injectMapLibreCSS()
  }
  injectAgentOSMapCSS()
  element.classList.add('agentos-sdk-map')
  if (!element.style.height) {
    element.style.height = '420px'
  }

  const config = await getMapConfig()
  const feature = listingToFeature(listing)
  const data: ListingGeoJSON = addListingLabels({ type: 'FeatureCollection', features: [feature], total: 1, truncated: false })
  const map = new maplibregl.Map({
    container: element,
    style: styleForMapTilerKey(config.maptilerKey),
    center: [listing.longitude, listing.latitude],
    zoom: options.zoom ?? 14,
    ...options.mapLibreOptions,
  })
  map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right')
  map.addControl(new maplibregl.ScaleControl({ unit: 'imperial' }), 'bottom-left')

  const openPopup = () => {
    new maplibregl.Popup({ closeButton: true, maxWidth: popupMaxWidth(options) })
      .setLngLat(feature.geometry.coordinates)
      .setHTML(listingPopupHTML(data.features[0].properties, options))
      .addTo(map)
    void track({ event: 'map_listing_popup_opened', listingId: listing.id, metadata: { providerId: listing.providerId, singleListing: true } }).catch(() => undefined)
  }

  map.on('load', () => {
    map.resize()
    ensureListingLayers(map, data, options)
    if (options.openPopup !== false) openPopup()
    void track('listing_map_loaded', { listingId: listing.id, providerId: listing.providerId }).catch(() => undefined)
  })
  map.on('styledata', () => ensureListingLayers(map, data, options))
  map.on('click', (event) => {
    if (!map.getLayer('agentos-listing-points')) return
    const clicked = map.queryRenderedFeatures(event.point, { layers: ['agentos-listing-points'] })[0]
    if (clicked) openPopup()
  })
  map.on('mousemove', (event) => {
    if (!map.getLayer('agentos-listing-points')) return
    const features = map.queryRenderedFeatures(event.point, { layers: ['agentos-listing-points'] })
    map.getCanvas().style.cursor = features.length > 0 ? 'pointer' : ''
  })
  const elementClickHandler = (event: MouseEvent) => trackListingLink(event, track)
  element.addEventListener('click', elementClickHandler)

  return {
    map,
    destroy: () => {
      map.remove()
      element.removeEventListener('click', elementClickHandler)
      element.classList.remove('agentos-sdk-map')
    },
  }
}

export function markFeatured(target: string | Element = '#featured', anchorId = featuredAnchorId) {
  const element = typeof target === 'string' ? document.querySelector(target) : target
  if (!element) return null

  const existing = document.getElementById(anchorId)
  if (existing) return anchorId

  const anchor = document.createElement('span')
  anchor.id = anchorId
  anchor.setAttribute('data-agentos-featured-anchor', 'true')
  anchor.style.position = 'relative'
  anchor.style.top = '24px'
  anchor.style.display = 'block'
  anchor.style.width = '1px'
  anchor.style.height = '1px'
  anchor.style.overflow = 'hidden'
  element.prepend(anchor)
  return anchorId
}

function mergeMapOptions(base: AgentOSMapOptions, override: AgentOSMapOptions): AgentOSMapOptions {
  return {
    ...base,
    ...override,
    markerColors: { ...base.markerColors, ...override.markerColors },
    popup: { ...base.popup, ...override.popup },
    mapLibreOptions: { ...base.mapLibreOptions, ...override.mapLibreOptions },
  }
}

function mergeSearchMapOptions(base: AgentOSMapOptions, override: ListingSearchMapOptions): ListingSearchMapOptions {
  return {
    ...mergeMapOptions(base, override),
    target: override.target,
    initialBounds: override.initialBounds,
    moveEndDebounceMs: override.moveEndDebounceMs,
  }
}

function listingToFeature(listing: Listing): ListingGeoJSONFeature {
  return {
    type: 'Feature',
    id: listing.id,
    geometry: { type: 'Point', coordinates: [listing.longitude, listing.latitude] },
    properties: { ...listing },
  }
}

function styleForMapTilerKey(key: string) {
  const trimmed = key.trim()
  return trimmed ? `https://api.maptiler.com/maps/streets-v2/style.json?key=${encodeURIComponent(trimmed)}` : 'https://demotiles.maplibre.org/style.json'
}

function boundsFromMap(map: MapLibreMap): MapBounds {
  const bounds = map.getBounds()
  return {
    west: roundCoord(bounds.getWest()),
    south: roundCoord(bounds.getSouth()),
    east: roundCoord(bounds.getEast()),
    north: roundCoord(bounds.getNorth()),
  }
}

function waitForMapLoad(map: MapLibreMap) {
  if (map.loaded()) return Promise.resolve()
  return new Promise<void>((resolve, reject) => {
    const onLoad = () => {
      map.off('error', onError)
      resolve()
    }
    const onError = (event: maplibregl.ErrorEvent) => {
      map.off('load', onLoad)
      reject(event.error ?? new Error('AgentOS map failed to load'))
    }
    map.once('load', onLoad)
    map.once('error', onError)
  })
}

function emptyListingGeoJSON(): ListingGeoJSON {
  return { type: 'FeatureCollection', features: [], total: 0, truncated: false }
}

function openListingPopup(
  map: MapLibreMap,
  event: maplibregl.MapMouseEvent,
  options: AgentOSMapOptions,
  track: (event: string | TrackEvent, metadata?: Record<string, unknown>) => Promise<unknown>,
) {
  if (!map.getLayer('agentos-listing-points')) return
  const feature = map.queryRenderedFeatures(event.point, { layers: ['agentos-listing-points'] })[0] as unknown as ListingGeoJSONFeature | undefined
  if (!feature?.geometry?.coordinates) return
  const properties = (feature.properties ?? {}) as ListingGeoJSONFeature['properties']
  new maplibregl.Popup({ closeButton: true, maxWidth: popupMaxWidth(options) })
    .setLngLat(feature.geometry.coordinates)
    .setHTML(listingPopupHTML(properties, options))
    .addTo(map)
  void track({ event: 'map_listing_popup_opened', listingId: properties.id, metadata: { providerId: properties.providerId } }).catch(() => undefined)
}

function updateListingCursor(map: MapLibreMap, event: maplibregl.MapMouseEvent) {
  if (!map.getLayer('agentos-listing-points')) return
  const features = map.queryRenderedFeatures(event.point, { layers: ['agentos-listing-points'] })
  map.getCanvas().style.cursor = features.length > 0 ? 'pointer' : ''
}

function trackListingLink(
  event: MouseEvent,
  track: (event: string | TrackEvent, metadata?: Record<string, unknown>) => Promise<unknown>,
) {
  const link = (event.target as Element | null)?.closest?.('[data-agentos-map-listing-link]') as HTMLAnchorElement | null
  if (!link) return
  void track({ event: 'map_listing_link_clicked', listingId: link.dataset.listingId, metadata: { providerId: link.dataset.providerId, href: link.href } }).catch(() => undefined)
}

function ensureListingLayers(map: MapLibreMap, data: ListingGeoJSON, options: AgentOSMapOptions) {
  if (!map.isStyleLoaded()) return
  const existingSource = map.getSource('agentos-listings') as maplibregl.GeoJSONSource | undefined
  if (existingSource) {
    existingSource.setData(data as never)
  } else {
    map.addSource('agentos-listings', {
      type: 'geojson',
      data: data as never,
      promoteId: 'id',
    })
  }
  if (!map.getLayer('agentos-listing-points')) {
    map.addLayer({
      id: 'agentos-listing-points',
      type: 'circle',
      source: 'agentos-listings',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 3, 4, 8, 7, 13, 11],
        'circle-color': markerColorExpression(options.markerColors, options.defaultMarkerColor) as never,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.5,
        'circle-opacity': 0.92,
      },
    })
  }
  if (!map.getLayer('agentos-listing-price-labels')) {
    map.addLayer({
      id: 'agentos-listing-price-labels',
      type: 'symbol',
      source: 'agentos-listings',
      minzoom: 8,
      layout: {
        'text-field': ['get', 'priceLabel'],
        'text-size': 11,
        'text-offset': [0, 1.35],
        'text-anchor': 'top',
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#111827',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.5,
      },
    })
  }
}

function markerColorExpression(colors: Record<string, string> = {}, fallback = '#7c3aed') {
  const merged = {
    Active: '#22c55e',
    'Coming Soon': '#f59e0b',
    Pending: '#38bdf8',
    ...colors,
  }
  const expression: unknown[] = ['match', ['get', 'status']]
  for (const [status, color] of Object.entries(merged)) {
    expression.push(status, color)
  }
  expression.push(fallback)
  return expression
}

function addListingLabels(data: ListingGeoJSON): ListingGeoJSON {
  return {
    ...data,
    features: data.features.map((feature) => ({
      ...feature,
      properties: {
        ...feature.properties,
        priceLabel: priceLabel(Number(feature.properties.price ?? 0)),
      },
    })),
  }
}

function listingPopupHTML(properties: ListingGeoJSONFeature['properties'], options: AgentOSMapOptions) {
  const popup = popupOptions(options)
  const href = options.listingUrlTemplate ? listingURL(options.listingUrlTemplate, properties) : ''
  const link = href
    ? `<a data-agentos-map-listing-link="true" data-listing-id="${escapeHTML(properties.id)}" data-provider-id="${escapeHTML(properties.providerId)}" href="${escapeHTML(href)}" style="display:inline-block;margin-top:12px;border-radius:999px;background:${escapeHTML(popup.accentColor)};color:#fff;padding:8px 12px;text-decoration:none;font-size:12px;font-weight:700;">${escapeHTML(popup.linkLabel)}</a>`
    : ''
  const template = popup.template ? renderTemplate(popup.template, properties) : defaultPopupTemplate(properties, popup)
  return `
    <div class="agentos-map-popup ${escapeHTML(popup.className)}" style="${popupWrapperStyle(popup)}">
      ${template}
      ${link}
    </div>
  `
}

function listingURL(template: string, properties: ListingGeoJSONFeature['properties']) {
  return template.replace(/\{([^}:]+)(?::([^}]+))?\}/g, (_match, path: string, format: string | undefined) => encodeURIComponent(formatTemplateValue(resolveTemplateValue(properties, path), format)))
}

function renderTemplate(template: string, properties: ListingGeoJSONFeature['properties']) {
  return template.replace(/\{([^}:]+)(?::([^}]+))?\}/g, (_match, path: string, format: string | undefined) => escapeHTML(formatTemplateValue(resolveTemplateValue(properties, path), format)))
}

function resolveTemplateValue(properties: ListingGeoJSONFeature['properties'], path: string): unknown {
  const aliases: Record<string, string> = {
    mls_id: 'providerId',
    listing_id: 'id',
    provider: 'provider',
  }
  const resolvedPath = aliases[path] ?? path
  let value: unknown = properties
  for (const part of resolvedPath.split('.')) {
    if (value == null) return ''
    // MapLibre serializes array and object feature properties before returning rendered features.
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value)
      } catch {
        return ''
      }
    }
    if (Array.isArray(value)) {
      value = value[Number(part)]
    } else if (typeof value === 'object') {
      value = (value as Record<string, unknown>)[part]
    } else {
      return ''
    }
  }
  return value ?? ''
}

function formatTemplateValue(value: unknown, format?: string) {
  if (value == null || value === '') return ''
  if (format === 'currency') {
    const number = Number(value)
    return Number.isFinite(number) && number > 0 ? currency(number) : 'Price unavailable'
  }
  if (format === 'number') {
    const number = Number(value)
    return Number.isFinite(number) ? number.toLocaleString() : String(value)
  }
  if (format === 'compactPrice') {
    return priceLabel(Number(value))
  }
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function defaultPopupTemplate(properties: ListingGeoJSONFeature['properties'], popup: NormalizedMapPopupOptions) {
  const image = formatTemplateValue(resolveTemplateValue(properties, popup.imageField))
  return `
    ${popup.showImage && image ? `<img src="${escapeHTML(image)}" alt="${escapeHTML(properties.address || 'Listing photo')}" style="display:block;width:calc(100% + ${cssSize(popup.padding)} * 2);height:${cssSize(popup.imageHeight)};object-fit:cover;margin:calc(${cssSize(popup.padding)} * -1) calc(${cssSize(popup.padding)} * -1) 12px;border-radius:${cssSize(popup.borderRadius)} ${cssSize(popup.borderRadius)} 0 0;" />` : ''}
    <div style="font-weight:800;margin-bottom:4px;">${escapeHTML(properties.address || 'Listing')}</div>
    <div style="font-size:12px;color:${escapeHTML(popup.mutedTextColor)};margin-bottom:8px;">${escapeHTML([properties.city, properties.state, properties.postalCode].filter(Boolean).join(', '))}</div>
    ${popup.showPrice ? `<div style="font-size:18px;font-weight:900;color:${escapeHTML(popup.accentColor)};margin-bottom:6px;">${Number(properties.price) > 0 ? currency(Number(properties.price)) : 'Price unavailable'}</div>` : ''}
    ${popup.showStats ? `<div style="font-size:12px;color:${escapeHTML(popup.textColor)};">${properties.beds || '-'} bd &middot; ${properties.baths || '-'} ba &middot; ${properties.sqft ? Number(properties.sqft).toLocaleString() : '-'} sqft</div>` : ''}
    ${popup.showStatus ? `<div style="font-size:11px;color:${escapeHTML(popup.mutedTextColor)};margin-top:6px;">${escapeHTML([properties.status, properties.propertyType].filter(Boolean).join(' · '))}</div>` : ''}
  `
}

function popupOptions(options: AgentOSMapOptions): NormalizedMapPopupOptions {
  const popup = options.popup ?? {}
  return {
    showPrice: popup.showPrice ?? true,
    showStats: popup.showStats ?? true,
    showStatus: popup.showStatus ?? true,
    showImage: popup.showImage ?? true,
    imageField: popup.imageField ?? 'media.0',
    imageHeight: popup.imageHeight ?? 140,
    fontFamily: popup.fontFamily ?? 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
    textColor: popup.textColor ?? '#18181b',
    mutedTextColor: popup.mutedTextColor ?? '#52525b',
    backgroundColor: popup.backgroundColor ?? '#ffffff',
    accentColor: popup.accentColor ?? '#0891b2',
    borderRadius: popup.borderRadius ?? 16,
    padding: popup.padding ?? 14,
    maxWidth: popup.maxWidth ?? 340,
    className: popup.className ?? '',
    linkLabel: popup.linkLabel ?? 'View listing',
    template: popup.template ?? '',
  }
}

function popupMaxWidth(options: AgentOSMapOptions) {
  return cssSize(popupOptions(options).maxWidth)
}

function popupWrapperStyle(popup: ReturnType<typeof popupOptions>) {
  return [
    `font-family:${popup.fontFamily}`,
    `color:${popup.textColor}`,
    `background:${popup.backgroundColor}`,
    `border-radius:${cssSize(popup.borderRadius)}`,
    `padding:${cssSize(popup.padding)}`,
    'min-width:220px',
    'overflow:hidden',
  ].join(';')
}

function cssSize(value: string | number) {
  return typeof value === 'number' ? `${value}px` : value
}

function currency(value: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
}

function priceLabel(price: number) {
  if (!Number.isFinite(price) || price <= 0) return ''
  if (price >= 1000000) return `$${(price / 1000000).toFixed(price >= 10000000 ? 0 : 1)}M`
  return `$${Math.round(price / 1000)}K`
}

function roundCoord(value: number) {
  return Number(value.toFixed(6))
}

function escapeHTML(value: unknown) {
  return String(value ?? '').replace(/[&<>"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[char] ?? char)
}

function injectMapLibreCSS() {
  if (document.getElementById('agentos-maplibre-css')) return
  const link = document.createElement('link')
  link.id = 'agentos-maplibre-css'
  link.rel = 'stylesheet'
  link.href = 'https://unpkg.com/maplibre-gl@5.24.0/dist/maplibre-gl.css'
  document.head.appendChild(link)
}

function injectAgentOSMapCSS() {
  if (document.getElementById('agentos-map-css')) return
  const style = document.createElement('style')
  style.id = 'agentos-map-css'
  style.textContent = `
    .agentos-sdk-map { position: relative; min-height: 320px; overflow: hidden; }
    .agentos-sdk-map .maplibregl-map { width: 100%; height: 100%; }
    .agentos-sdk-map .maplibregl-popup-content { border-radius: 16px; padding: 0; box-shadow: 0 18px 60px rgba(15, 23, 42, 0.22); overflow: hidden; }
    .agentos-sdk-map .maplibregl-popup-close-button { font-size: 18px; padding: 4px 8px; }
  `
  document.head.appendChild(style)
}

export function createLocalStorageListingSearchPreferenceStore({
  siteId,
  scope,
  version = 2,
  storage = globalThis.localStorage,
}: {
  siteId: string
  scope: string
  version?: number
  storage?: Storage
}): LocalSearchPreferences {
  const normalizedSiteID = siteId.trim()
  const normalizedScope = scope.trim()
  if (!normalizedSiteID) throw new Error('AgentOS listing search preference siteId is required')
  if (!normalizedScope) throw new Error('AgentOS listing search preference scope is required')
  if (!Number.isInteger(version) || version < 1) throw new Error('AgentOS listing search preference version must be a positive integer')
  const key = `agentos:listing-search:v${version}:${encodeURIComponent(normalizedSiteID)}:${encodeURIComponent(normalizedScope)}`
  return {
    load: async () => {
      const raw = storage.getItem(key)
      if (!raw) return null
      try {
        const parsed = JSON.parse(raw) as Partial<ListingSearchPreference>
        if (!parsed.search || !Number.isInteger(parsed.page) || Number(parsed.page) < 1) return null
        return {
          search: normalizeListingSearch(parsed.search),
          page: Number(parsed.page),
          bounds: parsed.bounds ? normalizeMapBounds(parsed.bounds) : undefined,
        }
      } catch {
        return null
      }
    },
    save: async (preference) => {
      if (!Number.isInteger(preference.page) || preference.page < 1) throw new Error('AgentOS listing search preference page must be a positive integer')
      storage.setItem(key, JSON.stringify({
        search: normalizeListingSearch(preference.search),
        page: preference.page,
        bounds: preference.bounds ? normalizeMapBounds(preference.bounds) : undefined,
      }))
    },
    clear: async () => {
      storage.removeItem(key)
    },
    reset: async () => {
      storage.removeItem(key)
    },
  }
}

function listingSearchQuery(search: ListingSearch, pageSize: number, offset: number, bounds?: MapBounds) {
  const query = new URLSearchParams()
  const scalarKeys = ['query', 'minPrice', 'maxPrice', 'beds', 'baths', 'sort'] as const
  for (const key of scalarKeys) {
    const value = search[key]
    if (value != null && value !== '') query.set(key, String(value))
  }
  const arrayKeys: Array<[keyof Pick<ListingSearch, 'counties' | 'cities' | 'statuses' | 'propertyTypes' | 'providerIds'>, string]> = [
    ['counties', 'county'],
    ['cities', 'city'],
    ['statuses', 'status'],
    ['propertyTypes', 'propertyType'],
    ['providerIds', 'providerId'],
  ]
  for (const [key, parameter] of arrayKeys) {
    search[key]?.forEach((value) => query.append(parameter, value))
  }
  if (bounds) {
    for (const key of ['west', 'south', 'east', 'north'] as const) query.set(key, String(bounds[key]))
    query.set('includeMap', 'true')
  }
  query.set('limit', String(pageSize))
  query.set('offset', String(offset))
  const rendered = query.toString()
  return rendered ? `?${rendered}` : ''
}

function normalizeMapBounds(bounds: MapBounds): MapBounds {
  const normalized = {
    west: Number(bounds.west),
    south: Number(bounds.south),
    east: Number(bounds.east),
    north: Number(bounds.north),
  }
  if (!Object.values(normalized).every(Number.isFinite)) throw new Error('AgentOS listing search bounds must be finite numbers')
  if (normalized.west < -180 || normalized.east > 180 || normalized.south < -90 || normalized.north > 90) {
    throw new Error('AgentOS listing search bounds must be valid longitude and latitude values')
  }
  if (normalized.west >= normalized.east || normalized.south >= normalized.north) {
    throw new Error('AgentOS listing search west/south bounds must be less than east/north bounds')
  }
  return normalized
}

function boundsToLngLatBounds(bounds: MapBounds): [[number, number], [number, number]] {
  return [[bounds.west, bounds.south], [bounds.east, bounds.north]]
}

function normalizeListingSearch(search: ListingSearch): ListingSearch {
  const normalized: ListingSearch = {}
  const query = search.query?.trim()
  if (query) normalized.query = query
  for (const key of ['minPrice', 'maxPrice', 'beds', 'baths'] as const) {
    const value = search[key]
    if (value != null && Number.isFinite(value)) normalized[key] = value
  }
  for (const key of ['counties', 'cities', 'statuses', 'propertyTypes', 'providerIds'] as const) {
    const values = search[key]
    if (values?.length) normalized[key] = [...new Set(values.map((value) => value.trim()).filter(Boolean))]
  }
  if (search.sort != null) {
    if (!listingSearchSorts.includes(search.sort)) throw new Error(`Unsupported AgentOS listing search sort: ${search.sort}`)
    normalized.sort = search.sort
  }
  return normalized
}

function normalizePagination(pagination: ListingSearchPagination): ListingSearchPagination {
  if (!Number.isInteger(pagination.page) || pagination.page < 1) throw new Error('AgentOS listing search page must be a positive integer')
  if (!Number.isInteger(pagination.pageSize) || pagination.pageSize < 1 || pagination.pageSize > 100) {
    throw new Error('AgentOS listing search pageSize must be an integer between 1 and 100')
  }
  return { ...pagination }
}

function cloneListingSearch(search: ListingSearch): ListingSearch {
  return {
    ...search,
    counties: search.counties ? [...search.counties] : undefined,
    cities: search.cities ? [...search.cities] : undefined,
    statuses: search.statuses ? [...search.statuses] : undefined,
    propertyTypes: search.propertyTypes ? [...search.propertyTypes] : undefined,
    providerIds: search.providerIds ? [...search.providerIds] : undefined,
  }
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError'
}

function toQuery(params: Record<string, unknown>) {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue
    if (typeof value === 'object') {
      query.set(key, JSON.stringify(value))
    } else {
      query.set(key, String(value))
    }
  }
  const rendered = query.toString()
  return rendered ? `?${rendered}` : ''
}

function inferAPIBaseURL() {
  const script = document.currentScript instanceof HTMLScriptElement ? document.currentScript.src : ''
  if (script) {
    try {
      const url = new URL(script, globalThis.location?.href)
      url.pathname = url.pathname.replace(/\/sdk\/[^/]+$/, '') || '/'
      url.search = ''
      url.hash = ''
      return url.toString().replace(/\/$/, '')
    } catch {
      // Fall through to origin below.
    }
  }
  return globalThis.location?.origin ?? ''
}

function publicURLToAPIBaseURL(value: string) {
  const trimmed = value.trim().replace(/\/$/, '')
  if (!trimmed) return ''
  try {
    const url = new URL(trimmed, globalThis.location?.href)
    if (url.pathname === '' || url.pathname === '/') {
      url.pathname = '/api'
      return url.toString().replace(/\/$/, '')
    }
  } catch {
    // Fall through to string handling below.
  }
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`
}

function setAnalyticsDisabledFromURL() {
  try {
    const params = new URLSearchParams(globalThis.location?.search ?? '')
    const analytics = params.get('agentosAnalytics')
    if (params.has('agentosPreview') || analytics === 'off' || analytics === 'false' || analytics === '0') {
      localStorage.setItem(analyticsDisabledStorageKey, 'true')
    }
  } catch {
    // Ignore storage and URL access errors in embedded contexts.
  }
}

function analyticsDisabled() {
  try {
    return localStorage.getItem(analyticsDisabledStorageKey) === 'true'
  } catch {
    return false
  }
}

function getOrCreateVisitorId() {
  return storedId('agentos:visitorId')
}

function getOrCreateSessionId(timeoutMs: number) {
  const key = 'agentos:session'
  const legacyKey = 'agentos:sessionId'
  const now = Date.now()
  const current = readSession(key)
  if (current && now - current.lastSeenAt <= timeoutMs) {
    const next = { ...current, lastSeenAt: now }
    localStorage.setItem(key, JSON.stringify(next))
    return { id: current.id, isNew: false, reason: 'active' }
  }

  const previousSessionId = current?.id ?? localStorage.getItem(legacyKey) ?? undefined
  const next = crypto.randomUUID()
  localStorage.setItem(key, JSON.stringify({ id: next, createdAt: now, lastSeenAt: now }))
  localStorage.removeItem(legacyKey)
  return { id: next, isNew: true, reason: current ? 'expired' : 'missing_session', previousSessionId }
}

function storedId(key: string) {
  const current = localStorage.getItem(key)
  if (current) return { id: current, isNew: false }
  const next = crypto.randomUUID()
  localStorage.setItem(key, next)
  return { id: next, isNew: true }
}

function documentReferrer() {
  return globalThis.document?.referrer ?? ''
}

function readSession(key: string): { id: string; createdAt: number; lastSeenAt: number } | null {
  const raw = localStorage.getItem(key)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed.id === 'string' && typeof parsed.lastSeenAt === 'number') {
      return { id: parsed.id, createdAt: Number(parsed.createdAt) || parsed.lastSeenAt, lastSeenAt: parsed.lastSeenAt }
    }
  } catch {
    return null
  }
  return null
}

export default { createClient }
